import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { fromBaseQuantity, type BaseUnit, type IngredientPackage } from '@crave/costing';
import { Button } from '@crave/ui';
import { api } from '../api.ts';
import {
  MeasureIn,
  formatQty,
  useMeasureStyle,
  type MeasureStyle,
} from '../components/MeasureIn.tsx';
import { UnitSelect, defaultUnitValue, splitUnitValue } from '../components/UnitSelect.tsx';
import { Notice, Section, errorText, money } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

interface Item {
  ingredient_id: string;
  name: string;
  category: string | null;
  base_unit: BaseUnit;
  display_unit: string | null;
  density_g_per_ml: number | null;
  each_weight_g: number | null;
  packages: IngredientPackage[];
  on_hand_qty: number;
  planned_qty: number;
  for_orders_qty: number;
  for_prep_qty: number;
  keep_at_least_qty: number | null;
  on_list: boolean;
  to_buy_qty: number;
  buy_as: {
    source: 'last_purchase' | 'package';
    label: string;
    package_id: string | null;
    package_size: number;
    package_unit: string;
    package_qty: number;
    count: number;
    unit_price_cents: number | null;
  } | null;
  store: { id: string; name: string } | null;
  last_bought_at: string | null;
  estimated_cents: number | null;
}
interface ShoppingList {
  location: { id: string; slug: string; name: string };
  buffer_pct: number;
  orders: { count: number; last_pickup_at: string | null };
  items: Item[];
  to_make: { ingredient_id: string; name: string; base_unit: BaseUnit; qty: number }[];
}

const CSS = `
.sh-page { display: grid; gap: 26px; max-width: 760px; }
.sh-sub { margin: 4px 0 0; color: var(--ink-soft); font-size: 15px; line-height: 1.45; }
.sh-summary { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 6px 16px;
  padding: 18px 20px; border-radius: 16px; background: var(--paper); box-shadow: var(--shadow); }
.sh-summary strong { font-size: 28px; line-height: 1.1; letter-spacing: -.01em; }
.sh-summary span { color: var(--ink-soft); font-size: 15px; }
.sh-group { display: grid; gap: 6px; }
.sh-caption { display: flex; justify-content: space-between; gap: 12px; margin: 0; padding: 0 16px;
  font-size: 13px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: var(--crave-label, var(--ink-soft)); }
.sh-list { background: var(--paper); border-radius: 16px; overflow: hidden; box-shadow: var(--shadow); }
.sh-list:not(.sh-level) > * + * { box-shadow: inset 0 1px 0 var(--line); }
.sh-note { margin: 0; padding: 0 16px; font-size: 13px; line-height: 1.45; color: var(--ink-soft); }
.sh-row { display: flex; align-items: center; gap: 12px; min-height: 60px; padding: 8px 8px 8px 12px; }
.sh-check { flex: none; width: 44px; height: 44px; display: grid; place-items: center; border: 0; background: none;
  padding: 0; cursor: pointer; border-radius: 50%; }
.sh-check::before { content: ''; width: 24px; height: 24px; border-radius: 50%; border: 2px solid #c9c3ba; box-sizing: border-box; }
.sh-check[aria-checked='true']::before { border-color: var(--crave-red); background: var(--crave-red)
  url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M6.5 12.5l3.5 3.5 7.5-8' fill='none' stroke='white' stroke-width='2.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/20px no-repeat; }
.sh-check:disabled { cursor: default; opacity: .4; }
.sh-main { flex: 1; min-width: 0; display: grid; gap: 2px; text-align: left; border: 0; background: none; padding: 6px 0;
  font: inherit; color: inherit; cursor: pointer; min-height: 44px; }
.sh-name { font-weight: 600; line-height: 1.3; }
.sh-row[data-done] .sh-name { color: var(--ink-soft); text-decoration: line-through; text-decoration-color: #b8b2a8; }
.sh-detail { font-size: 14px; line-height: 1.35; color: var(--ink-soft); overflow-wrap: anywhere; }
.sh-amount { flex: none; text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; padding-right: 4px; }
.sh-chevron { flex: none; width: 8px; height: 13px; color: #b8b2a8; margin-right: 8px; }
.sh-make { display: flex; justify-content: space-between; gap: 12px; align-items: center; min-height: 52px; padding: 8px 16px; }
.sh-make strong { font-variant-numeric: tabular-nums; }
.sh-more { border: 0; background: none; color: var(--crave-red); font: inherit; font-weight: 600; min-height: 44px;
  padding: 0 16px; cursor: pointer; justify-self: start; }
.sh-bar { position: sticky; bottom: 0; z-index: 5; display: flex; justify-content: flex-end; gap: 12px; align-items: center;
  padding: 12px 0 calc(12px + env(safe-area-inset-bottom)); background: linear-gradient(transparent, var(--ivory) 30%); }
.sh-bar span { color: var(--ink-soft); font-size: 15px; margin-right: auto; }
.sh-empty { padding: 28px 20px; text-align: center; color: var(--ink-soft); }
.sh-empty strong { display: block; color: var(--ink); font-size: 18px; margin-bottom: 4px; }

.sh-sheet { border: 0; padding: 0; margin: auto; width: min(560px, calc(100vw - 24px)); max-height: calc(100dvh - 24px);
  border-radius: 18px; background: var(--ivory); color: var(--ink); box-shadow: 0 24px 60px rgba(20,20,20,.28);
  overflow: auto; overscroll-behavior: contain; }
.sh-sheet::backdrop { background: rgba(20,20,20,.45); }
.sh-sheet-body { display: grid; gap: 20px; padding: 20px; }
.sh-sheet-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.sh-sheet-head h2 { margin: 0; font-size: 22px; line-height: 1.25; }
.sh-facts { display: grid; }
.sh-fact { display: flex; justify-content: space-between; gap: 12px; min-height: 48px; align-items: center; padding: 6px 16px; }
.sh-fact span:last-child { color: var(--ink-soft); text-align: right; font-variant-numeric: tabular-nums; }
.sh-fact[data-total] span { color: var(--ink); font-weight: 700; }
.sh-switch { flex: none; position: relative; width: 51px; height: 31px; border-radius: 999px; border: 0; padding: 0;
  background: #e3dfd8; cursor: pointer; transition: background .2s ease; }
.sh-switch::after { content: ''; position: absolute; top: 2px; left: 2px; width: 27px; height: 27px; border-radius: 50%;
  background: #fff; box-shadow: 0 3px 8px rgba(0,0,0,.15), 0 1px 1px rgba(0,0,0,.16); transition: transform .2s ease; }
.sh-switch[aria-checked='true'] { background: var(--crave-red); }
.sh-switch[aria-checked='true']::after { transform: translateX(20px); }
.sh-switch::before { content: ''; position: absolute; inset: -7px -4px; }
.sh-switch:disabled { opacity: .5; }
.sh-level { display: grid; gap: 10px; padding: 14px 16px 16px; }
.sh-level-inputs { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr); gap: 10px; align-items: end; }
.sh-input { width: 100%; min-width: 0; min-height: 44px; padding: 0 12px; border-radius: 10px; border: 1px solid var(--line);
  background: #fff; color: var(--ink); font: inherit; font-size: 16px; }
.sh-label { display: grid; gap: 4px; font-size: 14px; font-weight: 600; }
.sh-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 10px; }
.sh-buy { display: grid; grid-template-columns: minmax(0, 1fr) 88px 104px; gap: 10px; align-items: center; padding: 10px 16px; }
.sh-buy .sh-detail { font-size: 13px; }
.sh-buy-loose { grid-template-columns: minmax(0, 1fr) 88px 104px; }
.sh-buy-loose .sh-loose-unit { grid-column: 2 / 4; }
@media (max-width: 520px) {
  .sh-level-inputs { grid-template-columns: 1fr; }
  .sh-buy { grid-template-columns: minmax(0, 1fr) 72px 92px; gap: 8px; padding: 10px 12px; }
  .sh-actions > button { flex: 1; }
}
`;

const STORAGE = (slug: string) => `crave.shopping.${slug}`;

function readChecked(slug: string): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE(slug));
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}
function saveChecked(slug: string, ids: Set<string>) {
  try {
    localStorage.setItem(STORAGE(slug), JSON.stringify([...ids]));
  } catch {
    // Private windows: checks last for this visit only.
  }
}

const dayWords = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  });

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "3 × 1 lb bag" */
const buyWords = (b: NonNullable<Item['buy_as']>, count = b.count) => `${count} × ${b.label}`;

function Chevron() {
  return (
    <svg className="sh-chevron" viewBox="0 0 8 13" aria-hidden="true">
      <path
        d="M1.5 1.5 6.5 6.5l-5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="sh-sheet"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {open && (
        <div className="sh-sheet-body">
          <div className="sh-sheet-head">
            <h2 id={titleId}>{title}</h2>
            <Button variant="ghost" onClick={onClose}>
              Done
            </Button>
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}

/** The shopping list (#53): what to buy for upcoming orders and minimum stock, by store. Owner only. */
export function Shopping() {
  const { can, location } = useStaff();
  const qc = useQueryClient();
  const [style] = useMeasureStyle();
  const slug = location.slug;
  const list = useQuery({
    queryKey: ['shopping', slug],
    queryFn: async () => (await api.get<ShoppingList>(`/admin/shopping?location=${slug}`)).data,
    enabled: can('shopping.manage'),
  });
  const [checked, setChecked] = useState(() => readChecked(slug));
  useEffect(() => setChecked(readChecked(slug)), [slug]);
  const [detail, setDetail] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [showStocked, setShowStocked] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  if (!can('shopping.manage'))
    return (
      <Section title="Shopping list">
        <Notice>Only the owner can use the shopping list.</Notice>
      </Section>
    );

  const items = list.data?.items ?? [];
  const toBuy = items.filter((i) => i.to_buy_qty > 0);
  const stocked = items.filter((i) => i.to_buy_qty <= 0);
  // Drop checks for items that are no longer on the list.
  const live = new Set([...checked].filter((id) => toBuy.some((i) => i.ingredient_id === id)));
  const toggle = (id: string) => {
    const next = new Set(live);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    saveChecked(slug, next);
    setChecked(next);
  };

  const stores = new Map<string, { name: string | null; items: Item[] }>();
  for (const i of toBuy) {
    const key = i.store?.id ?? '';
    const g = stores.get(key) ?? { name: i.store?.name ?? null, items: [] };
    g.items.push(i);
    stores.set(key, g);
  }
  const groups = [...stores.entries()].sort(([a, x], [b, y]) =>
    a === '' ? 1 : b === '' ? -1 : (x.name ?? '').localeCompare(y.name ?? ''),
  );
  const total = toBuy.reduce((s, i) => s + (i.estimated_cents ?? 0), 0);
  const unpriced = toBuy.filter((i) => i.estimated_cents === null).length;
  const selected = toBuy.filter((i) => live.has(i.ingredient_id));
  const open = detail ? items.find((i) => i.ingredient_id === detail) : undefined;
  const refresh = () => void qc.invalidateQueries({ queryKey: ['shopping', slug] });

  const orders = list.data?.orders;
  const why =
    orders && orders.count > 0
      ? `For ${plural(orders.count, 'upcoming order')}${orders.last_pickup_at ? ` through ${dayWords(orders.last_pickup_at)}` : ''}, with ${list.data?.buffer_pct ?? 0}% extra, plus the minimums you keep.`
      : 'No upcoming orders right now, so this is only what you keep on hand.';

  return (
    <div className="sh-page">
      <style href="crave-shopping" precedence="default">
        {CSS}
      </style>
      <div>
        <h1 style={{ margin: 0 }}>Shopping list</h1>
        <p className="sh-sub">
          {location.name}. {why}
        </p>
      </div>

      {list.isError && (
        <Notice kind="error">Could not load the list. {errorText(list.error, '')}</Notice>
      )}
      {done && (
        <div role="status">
          <Notice kind="ok">{done}</Notice>
        </div>
      )}
      {list.isPending && <p className="sh-sub">Loading…</p>}

      {list.isSuccess && (
        <>
          <div className="sh-summary">
            <strong>{toBuy.length === 0 ? 'All set' : plural(toBuy.length, 'item')}</strong>
            <span>
              {toBuy.length === 0
                ? 'Nothing to buy'
                : total > 0
                  ? `About ${money(total)}${unpriced ? `, plus ${plural(unpriced, 'item')} without a price` : ''}`
                  : 'No prices yet'}
            </span>
          </div>

          {list.data.to_make.length > 0 && (
            <section className="sh-group">
              <h2 className="sh-caption">
                <span>To make</span>
              </h2>
              <div className="sh-list">
                {list.data.to_make.map((m) => {
                  const ing = items.find((i) => i.ingredient_id === m.ingredient_id);
                  return (
                    <div className="sh-make" key={m.ingredient_id}>
                      <span>{m.name}</span>
                      <strong>{formatQty(m.qty, ing ?? { base_unit: m.base_unit }, style)}</strong>
                    </div>
                  );
                })}
              </div>
              <p className="sh-note">Their ingredients are already on the list below.</p>
            </section>
          )}

          {toBuy.length === 0 ? (
            <div className="sh-list sh-empty">
              <strong>Nothing to buy</strong>
              Stock covers every upcoming order and every minimum you set.
            </div>
          ) : (
            groups.map(([key, g]) => {
              const sum = g.items.reduce((s, i) => s + (i.estimated_cents ?? 0), 0);
              return (
                <section className="sh-group" key={key || 'none'}>
                  <h2 className="sh-caption">
                    <span>{g.name ?? 'No store yet'}</span>
                    {sum > 0 && <span>{money(sum)}</span>}
                  </h2>
                  <div className="sh-list">
                    {g.items.map((i) => (
                      <ItemRow
                        key={i.ingredient_id}
                        item={i}
                        style={style}
                        checked={live.has(i.ingredient_id)}
                        onToggle={() => toggle(i.ingredient_id)}
                        onOpen={() => setDetail(i.ingredient_id)}
                      />
                    ))}
                  </div>
                  {key === '' && (
                    <p className="sh-note">
                      {g.items.every((i) => !i.last_bought_at)
                        ? 'Never bought here yet. The store fills in after the first purchase.'
                        : 'No store on the last receipt. Pick the store next time you enter one.'}
                    </p>
                  )}
                </section>
              );
            })
          )}

          {stocked.length > 0 && (
            <section className="sh-group">
              <h2 className="sh-caption">
                <span>In stock</span>
              </h2>
              {showStocked ? (
                <div className="sh-list">
                  {stocked.map((i) => (
                    <div className="sh-row" key={i.ingredient_id}>
                      <button
                        type="button"
                        className="sh-main"
                        onClick={() => setDetail(i.ingredient_id)}
                      >
                        <span className="sh-name">{i.name}</span>
                        <span className="sh-detail">
                          {i.on_list
                            ? `${formatQty(i.on_hand_qty, i, style)} on hand${
                                i.keep_at_least_qty !== null
                                  ? ` · keep at least ${formatQty(i.keep_at_least_qty, i, style)}`
                                  : ''
                              }`
                            : 'Not on the shopping list'}
                        </span>
                      </button>
                      <Chevron />
                    </div>
                  ))}
                </div>
              ) : (
                <button type="button" className="sh-more" onClick={() => setShowStocked(true)}>
                  Show {plural(stocked.length, 'item')} in stock
                </button>
              )}
              <p className="sh-note">Tap an item to set the least you want on hand.</p>
            </section>
          )}

          <MeasureIn />

          {toBuy.length > 0 && (
            <div className="sh-bar">
              <span>
                {live.size} of {toBuy.length} in the cart
              </span>
              <Button
                disabled={selected.length === 0}
                onClick={() => {
                  setDone(null);
                  setReviewing(true);
                }}
              >
                I bought these
              </Button>
            </div>
          )}
        </>
      )}

      <Sheet open={Boolean(open)} title={open?.name ?? ''} onClose={() => setDetail(null)}>
        {open && list.data && (
          <ItemDetail
            item={open}
            style={style}
            locationId={list.data.location.id}
            bufferPct={list.data.buffer_pct}
            onSaved={refresh}
          />
        )}
      </Sheet>

      <Sheet open={reviewing} title="Add to stock" onClose={() => setReviewing(false)}>
        {reviewing && list.data && (
          <BoughtReview
            items={selected}
            locationId={list.data.location.id}
            onDone={(text) => {
              const next = new Set(live);
              for (const i of selected) next.delete(i.ingredient_id);
              saveChecked(slug, next);
              setChecked(next);
              setReviewing(false);
              setDone(text);
              refresh();
              void qc.invalidateQueries({ queryKey: ['stock'] });
              void qc.invalidateQueries({ queryKey: ['receipts'] });
            }}
          />
        )}
      </Sheet>
    </div>
  );
}

function ItemRow({
  item: i,
  style,
  checked,
  onToggle,
  onOpen,
}: {
  item: Item;
  style: MeasureStyle;
  checked: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const need = formatQty(i.to_buy_qty, i, style);
  return (
    <div className="sh-row" data-done={checked ? '' : undefined}>
      <button
        type="button"
        role="checkbox"
        className="sh-check"
        aria-checked={checked}
        aria-label={`${i.name} in the cart`}
        onClick={onToggle}
      />
      <button type="button" className="sh-main" onClick={onOpen}>
        <span className="sh-name">{i.buy_as ? `${i.name}: ${buyWords(i.buy_as)}` : i.name}</span>
        <span className="sh-detail">
          {i.buy_as
            ? `Need ${need}${i.buy_as.unit_price_cents !== null ? ` · ${money(i.buy_as.unit_price_cents)} each last time` : ''}`
            : `Need ${need}`}
        </span>
      </button>
      {i.estimated_cents !== null && <span className="sh-amount">{money(i.estimated_cents)}</span>}
      <Chevron />
    </div>
  );
}

function ItemDetail({
  item: i,
  style,
  locationId,
  bufferPct,
  onSaved,
}: {
  item: Item;
  style: MeasureStyle;
  locationId: string;
  bufferPct: number;
  onSaved: () => void;
}) {
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState(() => defaultUnitValue(i));
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const qtyId = useId();
  const listId = useId();
  const onList = useMutation({
    mutationFn: async (on: boolean) =>
      (await api.put(`/admin/shopping/items/${i.ingredient_id}`, { on_list: on })).data,
    onSuccess: onSaved,
    onError: (err) => setMsg({ kind: 'error', text: errorText(err) }),
  });
  const f = (n: number) => formatQty(n, i, style);
  const save = useMutation({
    mutationFn: async (quantity: number | null) =>
      (
        await api.put<{ keep_at_least_qty: number | null }>('/admin/shopping/levels', {
          location_id: locationId,
          ingredient_id: i.ingredient_id,
          quantity,
          ...(quantity === null ? {} : splitUnitValue(unit)),
        })
      ).data,
    onSuccess: (r) => {
      setQty('');
      setMsg({
        kind: 'ok',
        text:
          r.keep_at_least_qty === null
            ? 'Minimum cleared.'
            : `You will keep at least ${f(r.keep_at_least_qty)}.`,
      });
      onSaved();
    },
    onError: (err) => setMsg({ kind: 'error', text: errorText(err) }),
  });

  const facts: [string, string][] = [
    [`Upcoming orders (+${bufferPct}%)`, f(i.for_orders_qty)],
    ...(i.for_prep_qty > 0
      ? ([['To make prep items', f(i.for_prep_qty)]] as [string, string][])
      : []),
    ['Keep at least', i.keep_at_least_qty === null ? 'Not set' : f(i.keep_at_least_qty)],
    ['On hand', f(i.on_hand_qty)],
    ...(i.planned_qty > 0 ? ([['Already planned', f(i.planned_qty)]] as [string, string][]) : []),
  ];

  return (
    <>
      <div className="sh-list sh-facts">
        {facts.map(([k, v]) => (
          <div className="sh-fact" key={k}>
            <span>{k}</span>
            <span>{v}</span>
          </div>
        ))}
        <div className="sh-fact" data-total="">
          <span>To buy</span>
          <span>{i.to_buy_qty > 0 ? f(i.to_buy_qty) : 'Nothing'}</span>
        </div>
        {i.buy_as && i.to_buy_qty > 0 && (
          <div className="sh-fact">
            <span>In store sizes</span>
            <span>{buyWords(i.buy_as)}</span>
          </div>
        )}
        {i.store && (
          <div className="sh-fact">
            <span>Last bought</span>
            <span>
              {i.store.name}
              {i.last_bought_at ? `, ${dayWords(i.last_bought_at)}` : ''}
            </span>
          </div>
        )}
      </div>

      <div className="sh-list">
        <div className="sh-fact">
          <span id={listId}>On the shopping list</span>
          <button
            type="button"
            role="switch"
            className="sh-switch"
            aria-checked={i.on_list}
            aria-labelledby={listId}
            disabled={onList.isPending}
            onClick={() => onList.mutate(!i.on_list)}
          />
        </div>
      </div>
      <p className="sh-note" style={{ marginTop: -14 }}>
        Turn off for what you never buy, like tap water. This applies at every location.
      </p>

      <form
        className="sh-list sh-level"
        onSubmit={(e) => {
          e.preventDefault();
          setMsg(null);
          const n = Number(qty);
          if (!qty.trim() || !Number.isFinite(n) || n < 0) {
            setMsg({ kind: 'error', text: 'Enter how much to keep, like 2.' });
            return;
          }
          save.mutate(n === 0 ? null : n);
        }}
      >
        <strong>Keep at least</strong>
        <span className="sh-detail">
          The list buys enough to still have this much after every upcoming order. Just for this
          location.
        </span>
        <div className="sh-level-inputs">
          <label className="sh-label" htmlFor={qtyId}>
            Amount
            <input
              id={qtyId}
              className="sh-input"
              inputMode="decimal"
              placeholder={i.keep_at_least_qty === null ? 'e.g. 2' : 'New amount'}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </label>
          <UnitSelect ingredient={i} value={unit} onValueChange={(v) => setUnit(v)} label="Unit" />
        </div>
        {msg && (
          <div role="status">
            <Notice kind={msg.kind}>{msg.text}</Notice>
          </div>
        )}
        <div className="sh-actions">
          {i.keep_at_least_qty !== null && (
            <Button
              type="button"
              variant="secondary"
              disabled={save.isPending}
              onClick={() => save.mutate(null)}
            >
              Clear
            </Button>
          )}
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
      <p className="sh-note">
        Package sizes and allergens live on <Link to="/ingredients">Ingredients</Link>.
      </p>
    </>
  );
}

interface Line {
  item: Item;
  /** Packages bought, or for an item without a package size, the amount in `unit`. */
  count: string;
  /** Price for each package, or the total paid for a loose amount. */
  price: string;
  unit: string;
}

/** A starting amount in the picker's unit, rounded for typing over. */
function amountIn(item: Item, unitValue: string): string {
  const code = unitValue.startsWith('unit:') ? unitValue.slice(5) : item.base_unit;
  try {
    const n = fromBaseQuantity(item.to_buy_qty, code, item);
    // Up, so what is typed covers what the list needs.
    return String(Math.ceil(n * 100 - 1e-6) / 100);
  } catch {
    return '';
  }
}

function BoughtReview({
  items,
  locationId,
  onDone,
}: {
  items: Item[];
  locationId: string;
  onDone: (text: string) => void;
}) {
  const [lines, setLines] = useState<Line[]>(() =>
    items.map((item) => {
      if (item.buy_as)
        return {
          item,
          count: String(item.buy_as.count),
          price:
            item.buy_as.unit_price_cents === null
              ? ''
              : (item.buy_as.unit_price_cents / 100).toFixed(2),
          unit: '',
        };
      const unit = defaultUnitValue(item);
      return { item, count: amountIn(item, unit), price: '', unit };
    }),
  );
  const [error, setError] = useState<string | null>(null);
  const set = (id: string, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.item.ingredient_id === id ? { ...l, ...patch } : l)));

  const parsed = lines.map((l) => ({
    ...l,
    n: Number(l.count),
    cents: Math.round(Number(l.price.replace(/[$,\s]/g, '')) * 100),
  }));
  const valid = (p: (typeof parsed)[number]) =>
    Number.isFinite(p.n) &&
    p.n > 0 &&
    p.n <= (p.item.buy_as ? 1000 : 1_000_000) &&
    (p.item.buy_as || p.unit.startsWith('unit:')) &&
    p.price.trim() !== '' &&
    Number.isFinite(p.cents) &&
    p.cents >= 0;
  const lineTotal = (p: (typeof parsed)[number]) =>
    p.item.buy_as ? Math.round(p.n * p.cents) : p.cents;
  const total = parsed.reduce((s, p) => s + (valid(p) ? lineTotal(p) : 0), 0);

  const post = useMutation({
    mutationFn: async () => {
      const byStore = new Map<string, typeof parsed>();
      for (const p of parsed) {
        const key = p.item.store?.id ?? '';
        byStore.set(key, [...(byStore.get(key) ?? []), p]);
      }
      let receipts = 0;
      for (const [store, ps] of byStore) {
        await api.post('/admin/inventory/receipts', {
          location_id: locationId,
          vendor_id: store || null,
          notes: 'From the shopping list',
          items: ps.map((p) => {
            const b = p.item.buy_as;
            if (!b)
              // A loose amount: one "package" of exactly what was bought, at the total paid.
              return {
                ingredient_id: p.item.ingredient_id,
                package_count: 1,
                package_size: p.n,
                package_unit: p.unit.slice('unit:'.length),
                unit_price_cents: p.cents,
              };
            return {
              ingredient_id: p.item.ingredient_id,
              package_count: p.n,
              unit_price_cents: p.cents,
              ...(b.package_id
                ? { package_id: b.package_id }
                : {
                    package_size: b.package_size,
                    package_unit: b.package_unit,
                    description: b.label,
                  }),
            };
          }),
        });
        receipts += 1;
      }
      return receipts;
    },
    onSuccess: (receipts) =>
      onDone(
        `Added to stock: ${plural(lines.length, 'item')} on ${plural(receipts, 'receipt')}. Costs are updated too.`,
      ),
    onError: (err) => setError(errorText(err)),
  });

  return (
    <form
      style={{ display: 'grid', gap: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        const bad = parsed.find((p) => !valid(p));
        if (bad) {
          setError(
            bad.item.buy_as
              ? `Check ${bad.item.name}: enter how many and the price for each.`
              : `Check ${bad.item.name}: enter the amount, its unit and what you paid.`,
          );
          return;
        }
        post.mutate();
      }}
    >
      <p className="sh-note" style={{ padding: 0 }}>
        Change anything that was different at the store. Each store becomes a receipt in Purchasing.
      </p>
      <div className="sh-list">
        <div className="sh-buy sh-detail" aria-hidden="true">
          <span>Item</span>
          <span>How many</span>
          <span>Price</span>
        </div>
        {lines.map((l) => {
          const b = l.item.buy_as;
          const what = b ? b.label : 'loose amount';
          return (
            <div className={b ? 'sh-buy' : 'sh-buy sh-buy-loose'} key={l.item.ingredient_id}>
              <span style={{ minWidth: 0 }}>
                <span className="sh-name" style={{ display: 'block' }}>
                  {l.item.name}
                </span>
                <span className="sh-detail">
                  {b ? `${b.label}, price each` : 'Amount bought, total paid'}
                  {l.item.store ? ` · ${l.item.store.name}` : ''}
                </span>
              </span>
              <input
                className="sh-input"
                inputMode="decimal"
                aria-label={`How many ${what} of ${l.item.name}`}
                value={l.count}
                onChange={(e) => set(l.item.ingredient_id, { count: e.target.value })}
              />
              <input
                className="sh-input"
                inputMode="decimal"
                placeholder="$0.00"
                aria-label={
                  b
                    ? `Price for each ${b.label} of ${l.item.name}`
                    : `Total paid for ${l.item.name}`
                }
                value={l.price}
                onChange={(e) => set(l.item.ingredient_id, { price: e.target.value })}
              />
              {!b && (
                <div className="sh-loose-unit">
                  <UnitSelect
                    ingredient={l.item}
                    value={l.unit}
                    includePackages={false}
                    showUnavailableHint={false}
                    label="Unit"
                    onValueChange={(v) =>
                      set(l.item.ingredient_id, { unit: v, count: amountIn(l.item, v) })
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
        <div className="sh-fact" data-total="">
          <span>Total</span>
          <span>{money(total)}</span>
        </div>
      </div>
      {error && (
        <div role="alert">
          <Notice kind="error">{error}</Notice>
        </div>
      )}
      <div className="sh-actions">
        <Button type="submit" disabled={post.isPending}>
          {post.isPending ? 'Adding…' : 'Add to stock'}
        </Button>
      </div>
    </form>
  );
}
